import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconBraces, IconPlus } from '@tabler/icons-react'

import {
  declareVariable,
  fetchVariableVersions,
  resetVariable,
  revertVariable,
  saveVariable,
} from '@/reducers/voiceAgents/voiceAgents.actions'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'
import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { Pill } from '@/components/pills/Pill'

import OverrideEditor from './OverrideEditor'
import AddVariableModal from './AddVariableModal'

// Variables tab scoping. Node view = vars this node declares/references; global
// view = every variable on the agent (so a freshly added var is visible here
// even before any node references it).
const VIEW_NODE = 'node'
const VIEW_GLOBAL = 'global'

// The `{}` glyph mirrors the variable mock; a neutral gray pill flags required
// vars without competing with the brand-colored "All edits" affordance.
const VariableIcon = () => <IconBraces size={14} />

const VariableName = ({ name }) => (
  <code className="voice-agents__variable-row-name">
    ${'{'}
    {name}
    {'}'}
  </code>
)

// =============================================================================
// VariablesTab — declared variables for the current node.
//
// Editable rows wrap OverrideEditor; runtime-injected rows stay display-only.
// The "Add variable" button declares a new editable variable on the agent
// (POST /prompts/variables → declareVariable thunk); the new row surfaces here
// once the prompt references it (or the node declares it).
// =============================================================================

const VAR_REF_RE = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g

// Names referenced inside the node's prompt content. We pull from both
// promptText and segment content so vars inside <system> blocks still
// register. Returns a Set of identifiers.
const collectReferencedNames = (node) => {
  const found = new Set()
  if (!node?.config) {
    return found
  }
  const sources = []
  if (typeof node.config.promptText === 'string') {
    sources.push(node.config.promptText)
  }
  if (Array.isArray(node.config.segments)) {
    node.config.segments.forEach((segment) => {
      if (segment && typeof segment.content === 'string') {
        sources.push(segment.content)
      }
    })
  }
  sources.forEach((text) => {
    let match = VAR_REF_RE.exec(text)
    while (match) {
      found.add(match[1])
      match = VAR_REF_RE.exec(text)
    }
  })
  return found
}

// Display value precedence: the current (override) value, else the engineer
// default, else empty.
const resolveDisplayValue = (variable) => {
  if (!isNil(variable.currentValue)) {
    return String(variable.currentValue)
  }
  if (!isNil(variable.defaultValue)) {
    return String(variable.defaultValue)
  }
  return ''
}

const EditableVariableRow = ({ variable, isRequired }) => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const versions = useSelector(
    (state) => state.voiceAgents.versionsByVariableName[variable.name] || []
  )
  const isSaving = useSelector((state) => Boolean(state.voiceAgents.loading.saveVariable))
  const isReverting = useSelector((state) => Boolean(state.voiceAgents.loading.revertVariable))
  const isResetting = useSelector((state) => Boolean(state.voiceAgents.loading.resetVariable))

  useEffect(() => {
    if (agentId && variable.name && isEmpty(versions)) {
      dispatch(fetchVariableVersions(agentId, variable.name))
    }
  }, [agentId, variable.name, versions.length, dispatch])

  const handleSave = (nextValue) => dispatch(saveVariable(agentId, variable.name, nextValue))
  const handleRevert = (version) => dispatch(revertVariable(agentId, variable.name, version))
  const handleReset = () => dispatch(resetVariable(agentId, variable.name))

  const displayValue = resolveDisplayValue(variable)

  return (
    <li
      className="voice-agents__variable-row"
      data-testid={`variable-row-${variable.name}`}
      data-editable="true"
    >
      <OverrideEditor
        testIdPrefix={`variable-${variable.name}`}
        icon={<VariableIcon />}
        label={<VariableName name={variable.name} />}
        nameBadge={
          isRequired ? (
            <Pill xs dataTestId={`variable-${variable.name}-required`}>
              required
            </Pill>
          ) : null
        }
        displayName={variable.name}
        description={variable.description}
        value={displayValue}
        emptyHint={variable.defaultValue ? `Default: ${variable.defaultValue}` : 'No value set'}
        editorRows={3}
        versions={versions}
        hasOverride={Boolean(variable.hasOverride)}
        isSaving={isSaving}
        isReverting={isReverting}
        isResetting={isResetting}
        onSave={handleSave}
        onRevert={handleRevert}
        onReset={handleReset}
      />
    </li>
  )
}

// Runtime-injected (read-only) — same card shell + `{}` avatar as editable
// rows, but with no value box, no "All edits", and no edit affordance. The
// tinted background + missing edit controls signal "view-only category".
const RuntimeVariableRow = ({ variable, isRequired }) => (
  <li
    className={classNames('voice-agents__variable-row', 'voice-agents__variable-row--runtime')}
    data-testid={`variable-row-${variable.name}`}
    data-editable="false"
  >
    <div className="voice-agents__override-editor">
      <div className="voice-agents__override-editor-head">
        <div className="voice-agents__override-editor-title">
          <span className="voice-agents__override-editor-avatar">
            <VariableIcon />
          </span>
          <span className="voice-agents__override-editor-label">
            <VariableName name={variable.name} />
          </span>
          {isRequired && (
            <Pill xs dataTestId={`variable-${variable.name}-required`}>
              required
            </Pill>
          )}
        </div>
      </div>
      {variable.description && (
        <div
          className="voice-agents__override-editor-readonly-desc"
          data-testid={`variable-${variable.name}-description`}
        >
          {variable.description}
        </div>
      )}
    </div>
  </li>
)

const VariablesTab = ({ node }) => {
  const dispatch = useDispatch()
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const isAdding = useSelector((state) => Boolean(state.voiceAgents.loading.declareVariable))
  const [addOpen, setAddOpen] = useState(false)
  const [view, setView] = useState(VIEW_NODE)
  const declared = node?.config?.requiredVars || []

  // Node view = (node's declared required_vars) ∪ (editable agent variables the
  // node's prompt references). The second branch matters because PATCH /nodes
  // never mutates required_vars — so a variable referenced from the prompt would
  // otherwise stay hidden until someone re-seeds.
  const nodeVariables = useMemo(() => {
    const declaredEntries = declared.map((name) => {
      const found = (agent?.variables || []).find((entry) => entry.name === name)
      return found || { name, editable: false }
    })
    if (!agent || !Array.isArray(agent.variables)) {
      return declaredEntries
    }
    const declaredSet = new Set(declared)
    const referenced = collectReferencedNames(node)
    const fromPrompt = agent.variables.filter(
      (entry) =>
        entry.editable === true && !declaredSet.has(entry.name) && referenced.has(entry.name)
    )
    return [...declaredEntries, ...fromPrompt]
  }, [agent, declared, node])

  // A variable is "required" when the node declares it in requiredVars; that
  // drives the neutral "required" pill in the card header.
  const requiredSet = useMemo(() => new Set(declared), [declared])

  const globalVariables = agent?.variables || []
  const isGlobalView = view === VIEW_GLOBAL
  const variables = isGlobalView ? globalVariables : nodeVariables

  const editableVars = variables.filter((variable) => variable.editable === true)
  const runtimeVars = variables.filter((variable) => variable.editable !== true)

  const existingNames = globalVariables.map((variable) => variable.name)

  const handleAddVariable = async ({ name, defaultValue, description }) => {
    const result = await dispatch(
      declareVariable(agentId, name, { defaultValue, description, type: 'string', scope: 'global' })
    )
    if (result !== null) {
      setAddOpen(false)
      // The new var isn't referenced by this node yet, so surface it under the
      // global view instead of leaving the user on an unchanged node list.
      setView(VIEW_GLOBAL)
    }
  }

  const viewToggle = (
    <SegmentedControl
      items={[
        {
          accessor: VIEW_NODE,
          label: 'This node',
          active: !isGlobalView,
          onClick: () => setView(VIEW_NODE),
          dataTestId: 'variables-view-node',
        },
        {
          accessor: VIEW_GLOBAL,
          label: 'All variables',
          active: isGlobalView,
          onClick: () => setView(VIEW_GLOBAL),
          dataTestId: 'variables-view-global',
        },
      ]}
    />
  )

  const addButton = (
    <Button basic small onClick={() => setAddOpen(true)} data-testid="add-variable-button">
      <IconPlus /> Add variable
    </Button>
  )

  // Mounted only while open so the form state resets on each open.
  const addModal = addOpen ? (
    <AddVariableModal
      show
      busy={isAdding}
      existingNames={existingNames}
      onClose={() => setAddOpen(false)}
      onSubmit={handleAddVariable}
    />
  ) : null

  const emptyHint = isGlobalView
    ? 'This agent has no variables yet.'
    : 'This node does not declare any variables.'

  return (
    <div className="voice-agents__drawer-tab" data-testid="drawer-tab-variables">
      <div className="voice-agents__variable-list-header">
        {viewToggle}
        {addButton}
      </div>
      {isEmpty(variables) ? (
        <p className="voice-agents__drawer-tab-hint">{emptyHint}</p>
      ) : (
        <ul className="voice-agents__variable-list">
          {editableVars.map((variable) => (
            <EditableVariableRow
              key={variable.name}
              variable={variable}
              isRequired={requiredSet.has(variable.name)}
            />
          ))}
          {runtimeVars.map((variable) => (
            <RuntimeVariableRow
              key={variable.name}
              variable={variable}
              isRequired={requiredSet.has(variable.name)}
            />
          ))}
        </ul>
      )}
      {addModal}
    </div>
  )
}

export default VariablesTab
